/**
 * Single source of truth for all color values used in the Rana app and Chakra UI theme.
 *
 * Colors are organized into five sections — brand, semantic, background, button, and map —
 * mirroring the structure from `colors-consts.ts`.
 *
 * The `brand.*` / `map.*` token paths are resolved by Chakra UI so that JSX props like
 * `color="brand.clear_creek"` work out of the box. Nested objects (e.g. `brand.grey`)
 * are accessed with dot notation: `color="brand.grey.100"`.
 */

export const DEFAULT_COLORS = {
  brand: {
    bg: "white",
    deep_sea: "#2c3b45",
    keep_deep_sea: "#2c3b45",
    analytic: "#316489",
    analytic_hover: "#2E536D",
    keep_analytic: "#316489",
    clear_creek: "#69a0c9",
    clear_creek_bg: "#69a0c9",
    sky: "#aecce2",
    light_sky: "#e7f0f6",
    glacier: "#dee5ea",
    border: "#dee5ea",
    cloudy: "#fafdff",
    accent: "#E94E3D",
    selected: "#CFE1EE",
    corroded_orange: "#ff9450",
    modal_backdrop: "#2c3b45",
    grey: {
      100: "#fff",
      200: "#f8f8f8",
      300: "#ebebeb",
      400: "#d9d9d9",
      500: "#c2c2c2",
      600: "#a6a6a6",
      700: "#858585",
      800: "#474747",
      900: "#000",
    },
    gradient: {
      dark: "linear-gradient(to right, #69A0C9, #316489)",
      darkDark: "linear-gradient(to right, #316489, #2C3B45)",
      regular: "linear-gradient(to right, #FFFFFF, #69A0C9)",
      medium: "linear-gradient(to right, #FFFFFF, #AECCE2)",
      light: "linear-gradient(to right, #FFFFFF, #E7F0F6)",
    },
  },
  semantic: {
    success: {
      main: "#4CBF44",
      background: "#DBF8E5",
    },
    warning: {
      main: "#FFB546",
      background: "#FFFBF6",
    },
    error: {
      main: "#E94E3D",
      tint: "#FDEDEC",
      background: "#FEF6F5",
    },
    info: {
      main: "#69A0C9",
      background: "#F0F5FA",
    },
  },
  background: {
    primary: "#ffffff",
    secondary: "#69a0c9",
  },
  button: {
    primary: "#316489",
    tertiary: {
      hover: "#EDF2F7",
    },
  },
  map: {
    default: "#0059ff",
    selected: "#4BDC7E",
    border: "#787e96",
  },
  // FUTURE: Should be removed later
  drops_wise: {
    bg: "#094B6F",
  },
  transparent: "transparent",
  currentColor: "currentColor",
  white: "white",
} as const;

export type ColorTheme = typeof DEFAULT_COLORS;
export type BrandColorKey = keyof typeof DEFAULT_COLORS.brand;
export type MapColorKey = keyof typeof DEFAULT_COLORS.map;

// Type of all available color paths
export type ColorPath = `brand.${BrandColorKey}` | `map.${MapColorKey}`;

// Recursively maps all leaf values to `string` while preserving the structure shape,
// ensuring dark theme has the same keys as the default theme.
type DeepStringRecord<T> = {
  [K in keyof T]: T[K] extends object ? DeepStringRecord<T[K]> : string;
};

export const DEFAULT_DARK_COLORS: DeepStringRecord<typeof DEFAULT_COLORS> = {
  brand: {
    bg: "#316489",
    deep_sea: "#e7f0f6",
    keep_deep_sea: "#2c3b45",
    analytic: "#F0F5FA",
    analytic_hover: "#c9d5dcff",
    keep_analytic: "#316489",
    clear_creek: "#F0F5FA",
    clear_creek_bg: "#316489",
    sky: "#69a0c9",
    light_sky: "#29536C",
    glacier: "#c4d1db",
    border: "#69a0c9",
    cloudy: "#69a0c9",
    accent: "#ee8550",
    selected: "#1f3e50",
    corroded_orange: "#ff9450",
    modal_backdrop: "#316489",
    grey: {
      100: "#316489",
      200: "#2c3b45",
      300: "#26424f",
      400: "#204a59",
      500: "#296078",
      600: "#69a0c9",
      700: "#7eb1d4",
      800: "#97c0dd",
      900: "#b0cee6",
    },
    gradient: {
      dark: "linear-gradient(to right, #316489, #2C3B45)",
      darkDark: "linear-gradient(to right, #2C3B45, #1A1F27)",
      regular: "linear-gradient(to right, #2C3B45, #316489)",
      medium: "linear-gradient(to right, #2C3B45, #4A6B8A)",
      light: "linear-gradient(to right, #3D5A7A, #5A7FA8)",
    },
  },
  semantic: {
    success: {
      main: "#4ED37D",
      background: "#1a3a1a",
    },
    warning: {
      main: "#FFB546",
      background: "#443300",
    },
    error: {
      main: "#E94E3D",
      tint: "#E94E3D",
      background: "#411111",
    },
    info: {
      main: "#69A0C9",
      background: "#1a2a3a",
    },
  },
  background: {
    primary: "brand.analytic",
    secondary: "brand.analytic",
  },
  button: {
    primary: "#69a0c9",
    tertiary: {
      hover: "#29536c",
    },
  },
  map: {
    default: "#4f8cff",
    selected: "#2cb06f",
    border: "#444b61",
  },
  // FUTURE: Should be removed later
  drops_wise: {
    bg: "#094B6F",
  },
  transparent: "transparent",
  currentColor: "currentColor",
  white: "white",
};
